import { useState } from "react";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  Check,
  Linkedin,
  MapPin,
  Phone,
  Settings2,
  Users,
} from "lucide-react";
import { usePageSeoFor } from "@/lib/seo";
import { PAGE_SEO } from "@/data/pageSeo";
import { Link } from "@/rebuild/Navigation";
import { Shell, Close } from "@/rebuild/Shell";
import dominik from "@/assets/founder-dominik.webp";
import jake from "@/assets/founder-jake.webp";
import { useScrollEngine } from "./useScrollEngine";
import {
  ABOUT_FACTS,
  ABOUT_FOUNDERS,
  ABOUT_FOUNDERS_SECTION,
  ABOUT_ORIGIN,
} from "@/data/about";
import "./information.css";
import "./about-page.css";

// Copy comes from src/data/about.ts (the prerender prints the same records);
// the portraits are asset imports, so they are merged in here.
const portraits = {
  dominik: { photo: dominik, width: 996, height: 1232 },
  jake: { photo: jake, width: 1090, height: 1443 },
} as const;
const founders = ABOUT_FOUNDERS.map((founder) => ({
  ...founder,
  ...portraits[founder.id],
}));

const setupSteps = [
  {
    name: "Understand the work",
    description:
      "Start with the business, the people, and the calls you already receive.",
    title: "Your business comes first.",
    icon: Users,
    items: [
      [
        "Services & hours",
        "What you do, where you work, and when your team is available.",
      ],
      ["Common questions", "The information a caller needs before they book."],
      ["Current tools", "Your phone system, calendar, and customer records."],
    ],
    result: "A shared picture of what your front office needs.",
  },
  {
    name: "Build the conversation",
    description:
      "Configure the call flow around your services, booking rules, and team.",
    title: "Give every call a direction.",
    icon: Settings2,
    items: [
      [
        "Answer & qualify",
        "Define what the AI should ask and the details it should capture.",
      ],
      [
        "Booking rules",
        "Review availability, appointment types, and booking requirements.",
      ],
      ["Human handoffs", "Decide when a person should take over."],
    ],
    result: "A call flow built around the way you operate.",
  },
  {
    name: "Try the real scenarios",
    description:
      "Run test calls and review the details before the agent represents you.",
    title: "Work through the what-ifs.",
    icon: Phone,
    items: [
      [
        "Everyday inquiries",
        "Check the questions and appointment requests you expect.",
      ],
      [
        "Unclear requests",
        "Review the path when a caller needs something more specific.",
      ],
      [
        "Team escalation",
        "Test the agreed handoff when a call needs a person.",
      ],
    ],
    result: "A reviewed flow before a customer uses it.",
  },
  {
    name: "Keep the work visible",
    description: "Give your team the context to act on each call.",
    title: "A system you can look into.",
    icon: Check,
    items: [
      [
        "Conversations",
        "Review available call recordings and transcripts in your account.",
      ],
      [
        "Appointments",
        "See the booking records that connect a call to the calendar.",
      ],
      ["Your records", "Keep access to your data, with exportable records."],
    ],
    result: "Useful context for the people doing the work.",
  },
];

// The setup story in one glass panel: the four steps down the left, the
// sheet for the chosen step on the right, the panel itself never moving.
function SetupStory() {
  const [selected, setSelected] = useState(0);
  const step = setupSteps[selected];
  const Icon = step.icon;
  return (
    <section
      className="ab-setup g-tone"
      aria-labelledby="ab-setup-heading"
      data-sc-act="flow"
    >
      <div className="rb-wrap">
        <div className="sp-head" data-sc-in data-sc-stagger="70">
          <div>
            <p className="g-eyebrow">How we work together</p>
            <h2 id="ab-setup-heading">The setup is part of the product.</h2>
          </div>
          <p className="g-lede">
            Dominik and Jake are hands-on with the call flow, prompts, and test
            calls. Here’s what that means for your business.
          </p>
        </div>
        <div className="sp-panel g-panel" data-sc-in>
          <div
            className="sp-stages"
            role="group"
            aria-label="Explore how we set up your business"
          >
            {setupSteps.map((item, index) => (
              <button
                key={item.name}
                type="button"
                aria-pressed={selected === index}
                aria-controls="ab-setup-sheet"
                onClick={() => setSelected(index)}
              >
                <item.icon size={18} aria-hidden="true" />
                <span>
                  <strong>{item.name}</strong>
                  <small>{item.description}</small>
                </span>
                <ArrowRight size={16} aria-hidden="true" />
              </button>
            ))}
          </div>
          <div
            id="ab-setup-sheet"
            className="sp-sheet"
            key={selected}
            aria-live="polite"
            aria-atomic="true"
          >
            <div className="sp-sheet-head">
              <span className="g-panel-label">
                <Icon size={14} aria-hidden="true" /> Step {selected + 1} of{" "}
                {setupSteps.length}
              </span>
              <span className="g-panel-label">Illustrative</span>
            </div>
            <h3 className="g-h3">{step.title}</h3>
            <dl className="sp-fields">
              {step.items.map(([title, text]) => (
                <div key={title}>
                  <dt>{title}</dt>
                  <dd>{text}</dd>
                  <span className="g-check" aria-hidden="true">
                    <Check size={12} />
                  </span>
                </div>
              ))}
            </dl>
            <p className="sp-outcome">
              <span className="g-check" aria-hidden="true">
                <Check size={12} />
              </span>
              {step.result}
            </p>
          </div>
        </div>
        <div className="sp-foot" data-sc-in>
          <p className="g-note">
            Specific integrations and custom work are confirmed during scoping.
          </p>
          <Link className="g-btn g-btn--small" to="/book">
            Talk through your setup{" "}
            <ArrowUpRight size={15} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  const seo = PAGE_SEO["/about"];
  usePageSeoFor("/about");
  useScrollEngine();

  return (
    <Shell>
      <div className="ab">
        <section className="ab-hero g-band g-band--morning" data-sc-act="flow">
          <div className="ab-hero-grid rb-wrap">
            <div className="ab-hero-copy" data-sc-in data-sc-stagger="70">
              <p className="g-eyebrow">About Chleb AI</p>
              <h1>{seo.h1}</h1>
              <p className="g-lede">
                We know what it’s like to be busy doing the work while the next
                opportunity is calling.
              </p>
              <a href="#founders" className="g-btn g-btn--ghost">
                Meet Dominik & Jake <ArrowDown size={16} aria-hidden="true" />
              </a>
            </div>
            <div
              className="ab-portraits"
              aria-label="The founders of Chleb AI"
              data-sc-in
            >
              {founders.map((founder, i) => (
                <figure
                  key={founder.id}
                  className={
                    i ? "ab-frame g-glass ab-frame--second" : "ab-frame g-glass"
                  }
                >
                  <a
                    href={`#${founder.id}`}
                    aria-label={`Meet ${founder.name}`}
                  >
                    <img
                      src={founder.photo}
                      alt={founder.name}
                      width={founder.width}
                      height={founder.height}
                      decoding="async"
                    />
                  </a>
                  <figcaption>
                    <strong>{founder.name}</strong>
                    <span>{founder.role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
          <dl className="ab-facts rb-wrap" data-sc-in data-sc-stagger="60">
            {ABOUT_FACTS.map(([term, detail]) => (
              <div key={term}>
                <dt>{term}</dt>
                <dd>
                  {term === "Our home" ? (
                    <>
                      <MapPin size={15} aria-hidden="true" />{" "}
                    </>
                  ) : null}
                  {detail}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* The title of this page asks who runs the company, so the page
            answers it in one paragraph before the story starts.
            scripts/prerender.cjs stamps the same sentence into the static
            /about from the same src/data/pageSeo.ts string. */}
        <section className="ab-answer rb-wrap" aria-labelledby="ab-answer-heading" data-sc-act="flow">
          <div data-sc-in>
            <h2 id="ab-answer-heading">Who runs Chleb AI?</h2>
          </div>
          <div data-sc-in data-sc-stagger="70">
            <p>{seo.intro}</p>
          </div>
        </section>

        <section
          className="ab-origin rb-wrap"
          id="origin"
          aria-labelledby="ab-origin-heading"
          data-sc-act="flow"
        >
          <div data-sc-in>
            <h2 id="ab-origin-heading">
              {ABOUT_ORIGIN.heading[0]}
              <br />
              <span>
                {ABOUT_ORIGIN.heading[1]}
                <br />
                {ABOUT_ORIGIN.heading[2]}
              </span>
            </h2>
          </div>
          <div className="ab-origin-copy" data-sc-in data-sc-stagger="70">
            <p className="ab-large">{ABOUT_ORIGIN.lead}</p>
            {ABOUT_ORIGIN.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </section>

        <section
          className="ab-standard g-band g-band--midday"
          aria-labelledby="ab-standard-heading"
          data-sc-act="flow"
        >
          <div className="rb-wrap" data-sc-in data-sc-stagger="80">
            <p className="g-eyebrow">Our standard</p>
            <h2 id="ab-standard-heading">
              <span>Answer the customer.</span>
              <span>Tell the team what happened.</span>
            </h2>
            <div className="ab-standard-foot">
              <p>
                That’s the test for what we build. Useful systems should make
                the business easier to run and the day easier to see.
              </p>
              <Link className="g-btn g-btn--ghost g-btn--small" to="/why-us">
                More about our approach{" "}
                <ArrowUpRight size={16} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        <section
          className="ab-founders rb-wrap"
          id="founders"
          data-sc-act="flow"
          aria-labelledby="ab-founders-heading"
        >
          <div className="ab-founders-title" data-sc-in data-sc-stagger="70">
            <h2 id="ab-founders-heading">
              {ABOUT_FOUNDERS_SECTION.heading[0]}
              <br />
              {ABOUT_FOUNDERS_SECTION.heading[1]}
            </h2>
            <p>
              {ABOUT_FOUNDERS_SECTION.sub[0]}
              <br />
              {ABOUT_FOUNDERS_SECTION.sub[1]}
            </p>
          </div>
          {founders.map((founder) => (
            <article
              key={founder.id}
              className={`ab-founder ab-founder--${founder.id}`}
              id={founder.id}
              data-sc-in
              data-sc-stagger="90"
            >
              <figure className="ab-frame g-glass">
                <a href={founder.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${founder.name} on LinkedIn`}>
                  <img
                    src={founder.photo}
                    alt={`${founder.name}, ${founder.short} of Chleb AI`}
                    width={founder.width}
                    height={founder.height}
                    loading="lazy"
                    decoding="async"
                  />
                </a>
                <figcaption>
                  <span>{founder.headline}</span>
                </figcaption>
              </figure>
              <div className="ab-founder-bio">
                <p className="ab-founder-role">{founder.role}</p>
                <h3 className="g-h3">{founder.name}</h3>
                {founder.bio.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                <ul
                  className="ab-focus"
                  aria-label={`${founder.name} focus areas`}
                >
                  {founder.focus.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <a
                  href={founder.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="g-btn g-btn--ghost g-btn--small"
                >
                  <Linkedin size={15} aria-hidden="true" /> Connect with{" "}
                  {founder.name.split(" ")[0]}
                  <ArrowUpRight size={15} aria-hidden="true" />
                </a>
              </div>
            </article>
          ))}
        </section>

        <SetupStory />
        <Close title="Tell us what’s getting in the way." />
      </div>
    </Shell>
  );
}
